import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from './store';
import { routeForMember } from './hooks';
import type { LoginSession } from './types';

// ─── Types ─────────────────────────────────────────────────────

export interface MagicLinkRequestResult {
  message: string;
  /**
   * Development-only: the full URL. In production this is undefined
   * and the link is delivered by email.
   */
  devLink?: string;
}

// ─── API ───────────────────────────────────────────────────────

async function requestMagicLinkCall(email: string): Promise<MagicLinkRequestResult> {
  try {
    const res = await api.post('/auth/magic-link/request', { email });
    return (res.data?.data ?? res.data) as MagicLinkRequestResult;
  } catch (err) {
    throw new Error(getApiErrorMessage(err, 'Could not send sign-in link'));
  }
}

/**
 * `LoginSession`, not the `LoginResponse` union — and that is a statement about the server, not a
 * convenience.
 *
 * Magic-link sign-in NEVER returns an MFA challenge, because it is issued only to portal
 * solicitors: `requestMagicLink` filters on `memberType: 'solicitor'` (auth.service.ts) and
 * silently no-ops for every other member type, so a chambers account with two-factor on cannot
 * obtain a link at all. The emailed token is itself the factor for that trust domain.
 *
 * If magic links were ever opened to chambers staff, this type would be wrong and the compiler
 * would say so at the call sites below — which is the point of narrowing it here rather than
 * widening the union.
 */
async function verifyMagicLinkCall(token: string): Promise<LoginSession> {
  try {
    const res = await api.post('/auth/magic-link/verify', { token });
    return (res.data?.data ?? res.data) as LoginSession;
  } catch (err) {
    throw new Error(getApiErrorMessage(err, 'Sign-in link is invalid or expired'));
  }
}

// ─── Hooks ─────────────────────────────────────────────────────

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: (email: string) => requestMagicLinkCall(email),
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Request failed');
    },
  });
}

export function useVerifyMagicLink() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  return useMutation({
    mutationFn: (token: string) => verifyMagicLinkCall(token),
    onSuccess: (data) => {
      setSession(data);
      toast.success(`Welcome, ${data.user.firstName}`);
      navigate(routeForMember(data.user.memberType), { replace: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Sign-in failed');
    },
  });
}
